import { Link } from '@tanstack/react-router';
// Lucide dropped brand glyphs (Instagram, Twitter, …) — they were trademarked
// marks in an icon set that isn't licensed to redistribute them. Camera is the
// closest generic stand-in; swap in an inline SVG if the real mark matters.
import { Mail, Camera, Leaf } from 'lucide-react';
import { Wordmark } from '@/components/brand/logo';

const SUPPORT_EMAIL = 'sreerasaproducts@gmail.com';

/**
 * Category links carry `search`, not a `?category=` string baked into `to`.
 * TanStack Router validates search params against the route's schema, so a
 * hand-written query string is both untyped and a type error.
 */
const COLUMNS = [
  {
    heading: 'Shop',
    links: [
      { to: '/shop', label: 'All produce' },
      { to: '/shop', label: 'Mangoes', search: { category: 'mangoes' } },
      { to: '/shop', label: 'Vegetables', search: { category: 'vegetables' } },
      { to: '/shop', label: 'Rice & grains', search: { category: 'rice' } },
    ],
  },
  {
    heading: 'Company',
    links: [
      { to: '/about', label: 'Why buy direct' },
      { to: '/farms', label: 'Our farms' },
      { to: '/auth/sign-up', label: 'Sell with us' },
    ],
  },
] as const;

export function Footer() {
  return (
    <footer className="mt-24 border-t border-soil-200 bg-soil-950 bg-grain text-soil-300">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-12 md:grid-cols-[1.5fr_1fr_1fr_1.2fr]">
          {/* ── Brand ── */}
          <div>
            <Wordmark className="text-2xl" />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-soil-400">
              Fruits, vegetables, rice and grains from farms we name, delivered to your door.
              Nothing carbide-ripened. Nothing anonymous.
            </p>
            <p className="mt-5 flex items-center gap-2 text-xs text-farm-400">
              <Leaf className="size-3.5" aria-hidden />
              Farm to home, Telangana
            </p>
          </div>

          {/* ── Link columns ── */}
          {COLUMNS.map((column) => (
            <div key={column.heading}>
              <h2 className="font-display text-sm font-bold uppercase tracking-wider text-soil-100">
                {column.heading}
              </h2>
              <ul className="mt-4 space-y-2.5">
                {column.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      search={'search' in link ? link.search : undefined}
                      className="text-sm text-soil-400 transition-colors hover:text-harvest-400"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* ── Contact ── */}
          <div>
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-soil-100">
              Get in touch
            </h2>
            <ul className="mt-4 space-y-2.5">
              <li>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="flex items-center gap-2 text-sm text-soil-400 transition-colors hover:text-harvest-400"
                >
                  <Mail className="size-4 shrink-0" aria-hidden />
                  {SUPPORT_EMAIL}
                </a>
              </li>
              <li>
                <a
                  href="https://instagram.com/sreerasa"
                  target="_blank"
                  // noreferrer alongside noopener: target=_blank without it
                  // hands the new tab a window.opener handle back to us.
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-soil-400 transition-colors hover:text-harvest-400"
                >
                  <Camera className="size-4 shrink-0" aria-hidden />
                  @sreerasa
                </a>
              </li>
            </ul>

            <p className="mt-6 text-xs leading-relaxed text-soil-500">
              Farmers: write to us and we'll get you listed. You set the price.
            </p>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-soil-800 pt-8 sm:flex-row">
          <p className="text-xs text-soil-500">
            © {new Date().getFullYear()} SreeRasa. Grown by farmers, not factories.
          </p>
          <div className="flex gap-5 text-xs text-soil-500">
            <Link to="/about" className="transition-colors hover:text-soil-300">
              Terms
            </Link>
            <Link to="/about" className="transition-colors hover:text-soil-300">
              Privacy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
